/* eslint-disable no-use-before-define */
import { FormError } from '@redux/modules/form/types';
import { i18n } from 'config/locales';
import { ValidateErrorContext, BaseValidateModel } from 'forms/shared/types/validatejs';
import { REQUIRED } from 'constants/errors';
import { LocaleErrorObject, LocaleOptions } from './types';
import { includesAny } from './string/string';
import { DataAttributeType } from './attributes/types';
import { isDataAttributeObject } from './attributes/attributes';

export const buildFormErrors = (rawErrors: ValidateErrorContext[] | boolean): FormError[] => {
  if (typeof rawErrors === 'boolean') { return []; }
  return rawErrors.map((e) => buildFormError(e));
};

export const buildFormError = (rawError: ValidateErrorContext): FormError => ({
  attribute: buildAttributePath(rawError),
  ...getErrorCode(rawError),
});

/**
 * This removes base validator names from the attribute, such as ""
 */
export const removeValidatorFromAttribute = (attr: string) => attr.replace('.array', '').replace('.date', '').replace('.hasValue', '');

/**
 * buildAttributePath will attempt to build out the attribute path for the
 * validation error. This becomes useful as we can properly identify an error to
 * a specific field, versus seeing just an error.
 */
export const buildAttributePath = <Model extends BaseValidateModel>(
  rawError: ValidateErrorContext<Model>
): string => {
  const index = rawError.attributes?.$index ?? -1;
  const { error } = rawError;
  let attr = index !== -1 ? `[${index}].Item.` : '';
  attr = `${attr}${rawError.attribute}`;

  if (typeof error === 'object') {
    if (isLocaleErrorObject(error)) {
      return attr;
    }

    const childAttr = buildAttributePath(error);

    // This will work for most cases until it gets to Telephone for Identification.

    // List.items[0].childAttr
    if (childAttr.startsWith('[')) { return removeValidatorFromAttribute(`${attr}.items${childAttr}`); }
    // List.childAttr
    return removeValidatorFromAttribute(`${attr}.${childAttr}`);
  }

  if (typeof error === 'string') {
    return attr;
  }

  return '';
};

/**
 * Typeguard for localeErrorObject
 */
export const isLocaleErrorObject = (object: unknown): object is LocaleErrorObject => {
  const obj = object as Partial<LocaleErrorObject>;

  if (!obj) { return false; }

  if (typeof obj === 'object') {
    if (!obj.localeOptions) { return false; }
    if (typeof obj.localeOptions !== 'object') { return false; }
    if (obj.code === undefined || obj.code === null) { return false; }
    if (typeof obj.code !== 'string') { return false; }

    return true;
  }

  return false;
};

interface ErrorCodeContext {
  code: string
  localeOptions: LocaleOptions
}
/**
 * A deeply embedded object may contain the error code, so we fetch it.
 */
export const getErrorCode = (rawError: ValidateErrorContext): ErrorCodeContext => {
  const error = rawError.error;
  const context: ErrorCodeContext = {
    code: '',
    localeOptions: {},
  };

  if (typeof error === 'object') {
    if (isLocaleErrorObject(error)) {
      context.code = error.code;
      context.localeOptions = {
        ...context.localeOptions,
        ...error.localeOptions,
      };

      return context;
    }

    return getErrorCode(error);
  }

  if (typeof rawError.error === 'string') {
    context.code = rawError.error;

    return context;
  }

  return context;
};

export const FormErrorsUtil = (formErrors: FormError[] = []) => {
  // TODO: Consider making formErrors immutable.
  let uniques: ReturnType<typeof FESU>;
  const hasAttribute = (attribute: string) => formErrors.filter((e) => e.attribute === attribute).length > 0;

  /**
   * This will return a list of unique errors.
   */
  const getDistinct = () => {
    const uniqueMap = new Map();
    const uniqueErrors: FormError[] = [];

    if (uniques === undefined) {
      formErrors.forEach((e) => {
        const errorString = `${e.attribute}.${e.code}`;
        if (!uniqueMap.has(errorString)) {
          uniqueMap.set(errorString, true);
          uniqueErrors.push(e);
        }
      });
      uniques = FESU(uniqueErrors);
      return uniques;
    }
    return uniques;
  };

  const getUniqueAttributes = () => formErrors.map((e) => e.attribute).filter((v, i, a) => a.indexOf(v) === i);

  const get = () => formErrors;

  const map = (callback: (element: ReturnType<typeof FEU>, index: number) => unknown) => (
    formErrors.map((element, index) => callback(FEU(element), index))
  );

  const filter = (callback: (element: ReturnType<typeof FEU>, index: number) => boolean) => (
    FESU(formErrors.filter((element, index) => callback(FEU(element), index)))
  );

  /**
   * filterByAttribute handles the DataAttributeType. This will filter out the
   * entire errors list based on the attribute(s) passed into this function.
   */
  const filterByAttribute = (attribute: DataAttributeType, exactAttribute?: boolean) => {
    function exact(value = false) {
      return exactAttribute !== undefined ? exactAttribute : value;
    }

    function getErrorsFromString(string: string, exact2: boolean) {
      return exact2 === true && string !== ''
        ? formErrors.filter((e) => string?.split('.').every((item) => e.attribute?.split('.')?.includes(item)))
        : formErrors.filter((e) => e.attribute?.includes(string));
    }

    function isStringArray(array: unknown[]): array is string[] {
      return typeof array[0] === 'string';
    }

    // handles string type
    if (typeof attribute === 'string') { return FESU(getErrorsFromString(attribute, exact())); }

    // handles array types
    if (Array.isArray(attribute)) {
      if (attribute.length > 0) {
        // handles string[]
        if (isStringArray(attribute)) {
          return FESU(
            attribute
              .map((e) => getErrorsFromString(e, exact()))
              .reduce((acc, val) => acc.concat(val), [])
          );
        }

        // handles attributeObject[]
        return FESU(
          attribute
            .map((e) => getErrorsFromString(e.value, exact(e.isExact)))
            .reduce((acc, val) => acc.concat(val), [])
        );
      }

      // when array is empty, return empty array
      return FESU([]);
    }

    // handles attributeObject
    if (isDataAttributeObject(attribute)) {
      return FESU(getErrorsFromString(attribute.value, exact(attribute.isExact)));
    }

    // handles undefined / null
    return FESU([]);
  };

  /**
   * The validation model will spit out a lot of errors related to the
   * particular model. This function aims to reduce redundant error messages to
   * provide a more user friendly experience. This will prioritize required over
   * all other error messages and remove duplicates.
   */
  const reduceRedundantErrors = () => {
    const uniques2 = new Map();

    return FESU(
      formErrors.filter((error) => {
        const key = error.code + error.attribute;
        const hasRelatedRequired = formErrors.find(
          (item) => item.attribute === error.attribute && item.code === REQUIRED
        );

        // This prevents duplicates
        if (uniques2.has(key)) { return false; }

        // Helps prioritizes required
        if (error.code === REQUIRED) {
          uniques2.set(key, key);
          return true;
        }

        // Prioritizes required
        if (hasRelatedRequired) {
          return false;
        }

        // If no required or duplicates, this should return true
        uniques2.set(key, key);
        return true;
      })
    );
  };

  /**
   * This will reduce duplicate errors within an attribute group. Instead of
   * having 10+ errors associated to "Date" as the user never filled it out
   * (such as Day: Required, Month: Required, Year: Required), this will reduce
   * it to one within that group.
   */
  const reduceDuplicateErrorsRelatedToGrouping = () => {
    const attributes = getUniqueAttributes();
    const groups: string[] = [];

    const getAttributePath = (attr: string, key: string) => {
      const keys = attr.split('.');
      const index = keys.indexOf(key) + 1;

      keys.splice(index, keys.length);

      return keys.join('.');
    };

    const appendGroup = (attr: string, key: string) => {
      const newAttribute = getAttributePath(attr, key);

      if (groups.includes(newAttribute)) { return; }

      groups.push(newAttribute);
    };

    // This will go through the attributes and group attributes up based on the type.
    attributes.forEach((attr) => {
      const keys = attr.split('.');
      const ppKey = keys[keys.length - 3];
      const pKey = keys[keys.length - 2];
      const lastKey = keys[keys.length - 1];

      // FieldModel
      if (lastKey === 'value') { return; }

      // DateModel
      if (includesAny(lastKey, ['day', 'month', 'year'])) {
        // DateRangeModel
        if (includesAny(pKey, ['from', 'to'])) {
          appendGroup(attr, ppKey);
        }

        appendGroup(attr, pKey);
      }

      // LocationModel
      if (includesAny(lastKey, ['city', 'country', 'street', 'street2', 'zipcode'])) {
        appendGroup(attr, pKey);
      }

      // TelephoneModel
      if (includesAny(lastKey, ['timeOfDay', 'type', 'number', 'extension'])) {
        appendGroup(attr, pKey);
      }

      // NameModel + SSN
      if (includesAny(lastKey, ['first', 'middle', 'last', 'suffix'])) {
        appendGroup(attr, pKey);
      }
    });

    let errors: FormError[] = [...formErrors];

    // This loops through each group and filters the errors array.
    groups.forEach((group) => {
      const groupErrors = errors.filter((e) => e.attribute === group);
      const childErrors = errors.filter((e) => e.attribute.includes(group));

      // This prioritizes the "e.attribute === group" errors.
      if (groupErrors.length > 0) {
        errors = errors.filter(
          (e) => e.attribute === group || (e.attribute !== group && !e.attribute.includes(group))
        );

        return;
      }

      // This handles the errors within an attribute group, but only for
      // REQUIRED. The likelyhood of duplicate errors existing within a grouped
      // attribute is low.
      if (childErrors.some((e) => e.code === REQUIRED)) {
        const targetError = childErrors.find((e) => e.code === REQUIRED);

        errors = errors.filter(
          (e) => e.attribute === targetError?.attribute
            || (e.attribute !== targetError?.attribute && !e.attribute.includes(group))
        );
      }
    });

    return FESU(errors);
  };

  const groupByListItem = () => {
    const groups: FormError[][] = [];
    const root: FormError[] = [];

    formErrors.forEach((e) => {
      const match = /\[(\d)*\]/g.exec(e.attribute);

      if (match) {
        const index = parseInt(match[1], 10);
        if (typeof groups[index] === 'undefined') {
          groups[index] = [];
        }
        groups[index].push(e);
      } else {
        root.push(e);
      }
    });

    return {
      groups,
      root,
      formErrors,
    };
  };

  /**
   * This is a helper function to filter out requiredErrors based on
   * showRequiredErrors.
   */
  const handleShowRequiredErrors = (showRequiredErrors: boolean, displayErrorsReviewOnly: string[] = []) => {
    if (showRequiredErrors === true) { return formErrors; }

    return formErrors.filter((error) => {
      const hasRelatedRequired = formErrors.find(
        (relatedError) => error.attribute === relatedError.attribute && relatedError.code.includes(REQUIRED)
      ) !== undefined;

      const hasReviewOnlyErrors = formErrors.find(
        (relatedError) => error.attribute === relatedError.attribute && displayErrorsReviewOnly.includes(relatedError.code)
      ) !== undefined;

      return !error.code.includes(REQUIRED) && !hasRelatedRequired && !hasReviewOnlyErrors;
    });
  };

  /**
   * Helper function to check the error list for specific errors. Useful for testing.
   */
  const hasErrorCode = (code: string, absoluteMatch = false): boolean => (
    formErrors.filter((err) => (absoluteMatch === true ? err.code === code : err.code.includes(code))).length > 0
  );

  const api = {
    hasAttribute,
    hasErrorCode,
    get,
    getDistinct,
    getUniqueAttributes,
    groupByListItem,
    filter,
    filterByAttribute,
    handleShowRequiredErrors,
    map,
    reduceRedundantErrors,
    reduceDuplicateErrorsRelatedToGrouping,
  };

  return api;
};

export const FormErrorUtil = (formError: FormError) => {
  let localeValid: boolean | null = null;

  const toString = () => `${formError.attribute}: ${formError.code}`;
  const isLocaleValid = () => {
    // FormErrors should be treated as immutable, thus, we store the
    // calculations and read off of that, instead of reprocessing.
    if (localeValid === null) {
      localeValid = i18n.value(formError.code) !== '';
      return localeValid;
    }

    return localeValid;
  };

  const api = {
    toString,
    isLocaleValid,
    ...formError,
  };

  return api;
};

const FESU = FormErrorsUtil;
const FEU = FormErrorUtil;
